import _ from 'lodash'
import moment from 'moment'
import pluralize from 'pluralize'
import React, { Component } from 'react'
import { MdEditDocument } from 'react-icons/md'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  origins,
  reasons,
  participants,
  outcomes
} from 'shared_src/dictionaries/encounters/EncounterDetails'
import { edit } from 'shared_src/encounter'

import { withLegacyRouter } from '../../../decorators'
import * as changes from '../../../lib/changes'
import * as Card from '../../shared/card'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import {
  bindTargetMixin,
  TextAreaInput,
  Validators,
  SaveButton,
  CancelButton
} from '../../shared/FormBuilder'
import MagicGrid from '../../shared/MagicGrid'
import {
  FloatingActionButton,
  Paper
} from '../../shared/NarusMaterial'
import EncounterNarrative from '../EncounterNarrative'
import EncounterSnapshot from '../EncounterSnapshot'
import {
  renderEncounterTime,
  renderUrgency,
  renderNarrative,
  renderEncounterType,
  renderPhoneEncounterStuff,
  renderDispositionAndReasonStuff,
  renderDoNotBill
} from '../shared'

import * as actions from './actions'

import './styles.scss'

const flatten = dictionary => {
  if (!_.isArray(dictionary) && _.isObject(dictionary)) {
    return _.uniq(
      Object.keys(dictionary)
        .reduce(
          (acc, key) => acc.concat(flatten(dictionary[key])),
          []
        )
    )
  }
  return dictionary
}

const lookup = (dictionary, value) =>
  value && dictionary.filter(v => v.value === value)
    .map(v => v.label)[0]

const WRAPPERS = {
  type: _.capitalize,
  origin: value => lookup(origins, value) || 'n/a',
  reason: value =>
    lookup(
      reasons.concat([{
        label: 'LHCM Summary',
        value: 'lhcm-summary'
      }]),
      value
    ),
  participant: value => lookup(flatten(participants), value),
  outcome: value => lookup(flatten(outcomes), value),
  urgency: _.capitalize,
  agentDoNotBill: value => value ? 'Yes' : 'No',
  timeBillable: value => (value && 'Billable') || 'Non-billable',
  timeOverridden: value => (value && 'Yes') || 'No',
  timeOverrideDuration: value =>
    value && pluralize('minute', Math.ceil(value), true),
  dateOfServiceOverridden: value => (value && 'Yes') || 'No',
  dateOfServiceOverrideDate: value =>
    (value && moment(value, 'YYYY-MM-DD')
      .format('MM/DD/YYYY')) || null
}

const reportIcon = (icon, title) => (
  <div
    className='nh-report-icon'
    title={title}
  >
    {icon}
  </div>
)

const tdstyle = {
  width: '25%',
  textAlign: 'center',
  padding: '0 10px',
  borderBottom: 0
}

const fhirName = hn =>
  _.compact(
    [ _.compact([ hn.given, hn.family ])
      .join(' ') ].concat(hn.suffix || [])
  )
    .join(', ')

const author = doc => fhirName(_.get(doc, 'document.user.user_metadata', {}))

class EncounterEditForm extends bindTargetMixin(Component) {
  constructor (props) {
    super(props)
    const document = _.get(props, 'encounter.document') || {}
    const value = {
      urgency: document.urgency,
      narrative: document.narrative,
      agentDoNotBill: document.agentDoNotBill
    }
    if (document.episodeOfCare) {
      value.type = document.type
      value.origin = document.origin
      value.reason = document.reason
      value.participant = document.participant
      value.outcome = document.outcome
    }
    if (document.timeBillable) {
      if (document.timeOverridden) {
        value.time = 'override'
        value.timeHours = Math.floor(
          document.timeOverrideDuration / 60
        )
          .toString()
        value.timeMinutes = (document.timeOverrideDuration % 60)
          .toString()
        value.timeDetails = document.timeOverrideComment
        if (document.dateOfServiceOverridden) {
          value.dateOfServiceOverridden = true
          value.dateOfServiceOverrideDate = moment(
            document.dateOfServiceOverrideDate,
            'YYYY-MM-DD'
          )
            .format('MM/DD/YYYY')
        }
      } else {
        value.time = 'computed'
      }
    } else {
      value.time = 'non-billable'
    }
    this.state = {
      value
    }
  }

  componentWillUnmount () {
    // this.props.onCancel()
  }

  render () {
    const { encounter, profile, clientName, clientAttributes, onCancel, onSave } = this.props
    const { value } = this.state
    const duration = Math.floor(encounter.document.timeDuration * 60)
    const organization = _.get(profile, 'app_metadata.partner_organization')
    const isNotAuthor = encounter.author_id !== profile.sub
    const role = _.get(profile, 'app_metadata.roles.meta.role')
    const isAdmin = [ 'administrator', 'executive_director', 'medical_director' ].includes(role)
    const showExtraInfo = !isNotAuthor || isAdmin
    const {
      document: { episodeOfCare }
    } = encounter

    const phoneEncounterTypes = [ 'phone', 'mobile', 'email' ]
    const patientEncounterTypes = [ ...phoneEncounterTypes, 'administrative' ]

    const showType = patientEncounterTypes.includes(encounter.document.type)
    const isCMS = encounter.document.type === 'cms'

    return (
      <form className='margin-top margin-bottom encounter-history-edit'>
        <h1>Edit Encounter Details</h1>
        {showType && episodeOfCare && renderEncounterType(clientName, profile)}
        {episodeOfCare && phoneEncounterTypes.includes(value.type) && renderPhoneEncounterStuff()}
        {episodeOfCare && phoneEncounterTypes.includes(value.type) &&
         renderDispositionAndReasonStuff({
           episodeOfCare,
           organization,
           encType: value.type,
           origin: value.origin,
           participant: value.participant,
           clientAttributes,
           reason: value.reason
         })}
        {showExtraInfo && !isCMS && renderUrgency(encounter)}
        {isAdmin && renderDoNotBill(encounter)}
        <div className='section'>
          {renderEncounterTime(value, duration)}
        </div>
        {showExtraInfo && !isCMS && renderNarrative(encounter)}
        <div className='row'>
          <div className='col-xs-12'>
            <TextAreaInput
              name='justification'
              label='Justification'
              validators={[ Validators.required() ]}
            />
          </div>
        </div>
        <div className='row end-xs'>
          <div className='col-xs-12'>
            <CancelButton
              formKey={this.props.formKey}
              onClick={onCancel}
            />

            <SaveButton
              formKey={this.props.formKey}
              onClick={onSave}
            />
          </div>
        </div>
      </form>
    )
  }
}

class EncounterHistoryItem extends React.Component {
  constructor (props) {
    super(props)
    this.onEdit = this.onEdit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onSave = this.onSave.bind(this)
  }

  onEdit () {
    const { onEdit, encounter } = this.props
    onEdit(encounter)
  }

  onCancel () {
    const { onCancel, encounter } = this.props
    onCancel(encounter)
  }

  onSave (data) {
    const { onSave, encounter } = this.props
    onSave(encounter, data)
  }

  renderHistory () {
    const {
      encounter: {
        document: { history }
      }
    } = this.props
    if (!_.isEmpty(history)) {
      const headers = [{
        displayFn: r => r.justification,
        title: 'Justification'
      },
      {
        displayFn: r => fhirName(_.get(r, 'author.user_metadata', {})),
        title: 'Author'
      },
      {
        displayFn: r => <FriendlyDateTime datetime={r.time} />,
        title: 'Edited Date/Time',
        sortBy: 'time'
      }]
      const rows = history.map(item => ({
        time: item.time,
        justification: item.justification,
        author: item.author,
        component: changes.render(item.time, item.changes, WRAPPERS)
      }))
      return (
        <div>
          <h2>History</h2>
          <MagicGrid
            rows={rows}
            headers={headers}
            canDelete={false}
            itemName='Encounters'
            sortOn={2}
            sortDir='desc'
          />
        </div>
      )
    }
    return null
  }

  renderSummary () {
    const { encounter, settings, profile } = this.props
    return (
      <div className='enc-doc-body'>
        {edit({
          encounter,
          settings,
          profile
        }) && (
          <FloatingActionButton onClick={this.onEdit}>
            <MdEditDocument color='white' />
          </FloatingActionButton>
        )}
        <EncounterNarrative encounter={encounter} />
        <EncounterSnapshot snapshot={encounter.document.endSnapshot} />
        {this.renderHistory()}
      </div>
    )
  }

  render () {
    const { edit, encounter, profile, clientName, clientAttributes } = this.props
    return (
      <Paper>
        {(edit && (
          <EncounterEditForm
            formKey='encounterEdit'
            encounter={encounter}
            profile={profile}
            clientName={clientName}
            clientAttributes={clientAttributes}
            onCancel={this.onCancel}
            onSave={this.onSave}
          />
        )) ||
          this.renderSummary()}
      </Paper>
    )
  }
}

class EncountersHistory extends React.Component {
  constructor (props) {
    super(props)
    this.onEdit = this.onEdit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onSave = this.onSave.bind(this)
  }

  componentDidMount () {
    const { dispatch, params } = this.props
    dispatch(actions.init(params.id))
  }

  onEdit (encounter) {
    const { dispatch } = this.props
    dispatch(actions.edit(encounter))
  }

  onCancel (encounter) {
    const { dispatch } = this.props
    dispatch(actions.cancel(encounter))
  }

  onSave (encounter, data) {
    const { dispatch } = this.props
    dispatch(actions.update(encounter, data))
  }

  getRows () {
    const { encountersHistory } = this.props
    const items = encountersHistory
    const {
      settings,
      profile,
      patient: { clientName },
      clientAttributes
    } = this.props
    if (!items || items.length < 1) {
      return []
    }
    return items.map(item => {
      const encounterTypes = {
        home: 'In Home Encounter',
        phone: 'Telephonic Encounter',
        email: 'Email Encounter',
        mobile: 'Mobile Encounter',
        administrative: 'Administrative Update',
        grandRounds: 'Grand Rounds',
        cms: 'Care Management Summary'
      }
      if (item.document.reportType === 'Research') { // Update to newly requested report display name in encounter history
        item.document.reportType = 'Research'
      }
      const encounterType = item.document.reportType ? item.document.reportType : encounterTypes[item.document.type] || 'Unknown Encounter'
      const edit = this.props.encountersEdit[item.id]

      return {
        type: encounterType,
        author: author(item),
        endTime: item.document.encounterEndTime,
        component: (
          <EncounterHistoryItem
            key={item.id}
            encounter={item}
            settings={settings}
            profile={profile}
            clientName={clientName}
            clientAttributes={clientAttributes}
            edit={edit}
            onEdit={this.onEdit}
            onCancel={this.onCancel}
            onSave={this.onSave}
          />
        )
      }
    })
  }

  render () {
    const clientName = this.props.params.clientName

    const headers = [
      {
        displayFn: r => (
          <div
            className='row middle-xs'
            style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <div>{r.type}</div>
            {!_.isEmpty(r.component.props.encounter.document.history) && (
              <div
                className='svg-small-edit-icon'
                style={{
                  marginLeft: '5px',
                  marginRight: '50px'
                }}
              />
            )}
          </div>
        ),
        title: 'Type'
      },
      {
        displayFn: r => r.author,
        title: 'Author'
      },
      {
        displayFn: r => <FriendlyDateTime datetime={r.endTime} />,
        title: 'End Time',
        sortBy: 'endTime'
      },
      {
        displayFn: r => {
          const patient = r.component.props.encounter.subject_id
          const careSummary =
            r.component.props.encounter.document.careplanReportId

          const enSummary =
            r.type === 'Grand Rounds'
              ? r.component.props.encounter.document.grandRoundsReportId
              : r.component.props.encounter.document
                .encounterSummaryReportId

          const patientSummary =
            r.component.props.encounter.document.patientSummaryReportId

          let summary

          if (r.type === 'Grand Rounds') {
            summary = (
              <Link
                to={`/patient/${clientName}/${patient}/report/${enSummary}/grandRounds`}
              >
                {reportIcon('GR', 'Grand Rounds')}
              </Link>
            )
          } else if (r.type === 'Care Management Summary') {
            summary = (
              <Link
                to={`/patient/${clientName}/${patient}/report/${r.component.props.encounter.document.monthlySummaryReportId}/monthlySummary`}
              >
                {reportIcon('CMS', 'Care Management Summary')}
              </Link>
            )
          } else if (r.type === 'APS') {
            summary = (
              <Link
                to={`/patient/${clientName}/${patient}/report/${r.component.props.encounter.document.apsReportId}/aps`}
              >
                {reportIcon('APS', 'APS Report')}
              </Link>
            )
          } else if (r.type === 'Research') {
            summary = (
              <Link
                to={`/patient/${clientName}/${patient}/report/${r.component.props.encounter.document.researchReviewReportId}/researchReview`}
              >
                {reportIcon('RR', 'Research Report')}
              </Link>
            )
          } else {
            summary = (
              <Link
                to={`/patient/${clientName}/${patient}/report/${enSummary}/encounterSummary`}
              >
                {reportIcon('ES', 'Encounter Summary')}
              </Link>
            )
          }

          const patientBrief = r.component.props.encounter.document
            .patientBriefReportId
            ? (
              <Link
                to={`/patient/${clientName}/${patient}/report/${r.component.props.encounter.document.patientBriefReportId}/patientBrief`}
              >
                {reportIcon('PB', 'Patient Brief')}
              </Link>
            )
            : null
          return (
            <div className='enc-history-reports'>
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <td style={tdstyle}>{summary}</td>
                    <td style={tdstyle}>
                      <Link
                        to={`/patient/${clientName}/${patient}/report/${careSummary}/carePlan`}
                      >
                        {reportIcon('CP', 'Care Plan')}
                      </Link>
                    </td>
                    <td style={tdstyle}>
                      <Link
                        to={`/patient/${clientName}/${patient}/report/${patientSummary}/patientSummary`}
                      >
                        {reportIcon('PS', 'Patient Summary')}
                      </Link>
                    </td>
                    <td style={tdstyle}>{patientBrief}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        },
        title: 'Reports'
      }]

    return (
      <div>
        {!this.props.modal
          ? (
            <Card.Card>

              <Card.Header>
                <h2>Encounter History</h2>
              </Card.Header>

              <div style={{ padding: '0 1rem 1rem' }}>
                <MagicGrid
                  rows={this.getRows()}
                  headers={headers}
                  canDelete={false}
                  itemName='Encounters'
                  sortOn={2}
                  sortDir='desc'
                />
              </div>
            </Card.Card>
          )
          : (
            <div style={{ padding: '0 1rem 1rem' }}>
              <MagicGrid
                rows={this.getRows()}
                headers={headers}
                canDelete={false}
                itemName='Encounters'
                sortOn={2}
                sortDir='desc'
              />
            </div>
          )}
      </div>
    )
  }
}

const mapStateToProps = redux => {
  const clientName = _.get(redux, 'patient.clientName')
  const clientAttributes = _.chain(redux)
    .get('globalVars.clients', [])
    .find(c => c.clientName === clientName)
    .get('attributes', '')
    .value()

  return {
    encountersHistory: redux.encountersHistory.data,
    encountersEdit: redux.encountersHistory.edit,
    patient: redux.patient,
    profile: redux.auth.profile,
    settings: _.get(redux, 'globalVars.settings', {}),
    clientAttributes
  }
}

export default connect(mapStateToProps)(withLegacyRouter(EncountersHistory))
